import type {
  Assignment, CoverageRequestType, Employee, Shift, TimeOff,
} from "./types";

// Shift topology for overtime-coverage. Today's target agency runs
// three 10-hour shifts back-to-back with 2-hour overlaps (Day 06-16,
// Evening 14-00, Night 22-08) and any given shift can be covered
// either by the shift before it or the shift after it — with
// wrap-around, so Night is "before" Day and Evening is "after" Day.
//
// Coverage is **scoped per department**. Patrol never covers Traffic,
// SROs never cover Detectives, etc. The "prev" and "next" shifts
// returned here are always in the same department as the target.
//
// This helper is the ONLY place the coverage topology is encoded.
// When we extend this into a per-agency configuration later, this is
// the function that gets swapped out.
export function adjacentShifts(
  shifts: Shift[],
  shiftId: string,
): { prev: Shift | null; next: Shift | null } {
  const target = shifts.find((s) => s.id === shiftId);
  if (!target || target.inRotation === false) return { prev: null, next: null };
  // Only shifts that are in the coverage rotation within this
  // department participate in the prev/next wrap. Specialty roles
  // (Traffic, SRO, K-9) that belong to the department but aren't
  // part of the 24/7 cycle are excluded by the inRotation flag.
  const pool = shifts
    .filter((s) => s.departmentId === target.departmentId && s.inRotation !== false)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const i = pool.findIndex((s) => s.id === shiftId);
  if (i < 0 || pool.length < 2) return { prev: null, next: null };
  const n = pool.length;
  return {
    prev: pool[(i - 1 + n) % n],
    next: pool[(i + 1) % n],
  };
}

// When an officer takes an OT row on a neighboring shift, their home
// shift cell becomes a contiguous extended span instead of just
// blank. Returns a short label like "21-11" (covering the first half
// of the next-shift) or "12-7" (covering the second half of the
// prev-shift). Returns null when the OT shift isn't an adjacent
// neighbor or the inputs don't add up.
export function extendedHomeRange(
  homeShift: Shift,
  otShift: Shift,
  otAssignment: Assignment,
  allShifts: Shift[],
): string | null {
  const { prev, next } = adjacentShifts(allShifts, homeShift.id);
  const isNext = next?.id === otShift.id;
  const isPrev = prev?.id === otShift.id;
  if (!isNext && !isPrev) return null;

  const note = (otAssignment.note ?? "").trim().toUpperCase();
  const isHalf = note === "OT½" || note === "OT/2";
  const halfMin = Math.round(otShift.durationMin / 2);

  const homeStart = parseHHMM(homeShift.startTime);
  const homeEnd = (homeStart + homeShift.durationMin) % (24 * 60);
  const otStart = parseHHMM(otShift.startTime);
  const otEnd = (otStart + otShift.durationMin) % (24 * 60);

  let extStart: number;
  let extEnd: number;
  if (isNext) {
    // Home then OT on the next shift → end extends.
    extStart = homeStart;
    extEnd = isHalf ? (otStart + halfMin) % (24 * 60) : otEnd;
  } else {
    // OT on the prev shift then home → start extends earlier.
    extStart = isHalf ? (otStart + halfMin) % (24 * 60) : otStart;
    extEnd = homeEnd;
  }
  return `${formatHour(extStart)}-${formatHour(extEnd)}`;
}

function parseHHMM(s: string): number {
  const [h, m] = s.split(":").map((n) => Number(n));
  return (h || 0) * 60 + (m || 0);
}

function formatHour(min: number): string {
  // Drop minutes for readability ("21-11" reads better than "21:00-11:00").
  // Round to the nearest hour so a 7:30 boundary doesn't disappear.
  const h = Math.round(min / 60) % 24;
  return String(h);
}

// True when the shift crosses midnight relative to its own start time
// (e.g. Night 22:00 + 10h = 32:00 → ends 08:00 the next calendar day).
// Used to decide whether a home-shift assignment's stored date matches
// the OT's cover date or the day before.
export function shiftWrapsMidnight(shift: Shift): boolean {
  return parseHHMM(shift.startTime) + shift.durationMin > 24 * 60;
}

// Resolve the calendar date on which the covering employee's HOME
// shift was recorded, given the OT cover's date. Two asymmetric
// midnight cases:
//
//   * First-half cover, home wraps midnight
//     E.g., Night home officer covers first half of Day gap.
//     Night started the previous calendar day, so the home cell —
//     and thus the extended-hours chip — sits on otDate - 1.
//
//   * Second-half cover, GAP wraps midnight
//     E.g., Day home officer covers second half of Night gap. The
//     Night gap (22:00 4/30 → 08:00 5/1) bleeds into 5/1, where
//     the Day officer's normal home shift starts at 06:00 5/1.
//     Their actual scheduled row is on 5/1, so the chip belongs
//     there — not on 4/30, where the gap was opened.
//
// Otherwise the home cell sits on otDate and we pass it through.
export function homeShiftDateForOT(
  homeShift: Shift,
  otShift: Shift,
  otDate: string,
  allShifts: Shift[],
): string | null {
  const { prev, next } = adjacentShifts(allShifts, homeShift.id);
  const isFirstHalfCover = next?.id === otShift.id;
  const isSecondHalfCover = prev?.id === otShift.id;
  if (!isFirstHalfCover && !isSecondHalfCover) return null;

  if (isFirstHalfCover && shiftWrapsMidnight(homeShift)) {
    return shiftYmd(otDate, -1);
  }
  if (isSecondHalfCover && shiftWrapsMidnight(otShift)) {
    return shiftYmd(otDate, +1);
  }
  return otDate;
}

function shiftYmd(ymd: string, delta: number): string {
  const d = new Date(ymd + "T00:00:00");
  d.setDate(d.getDate() + delta);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// The calendar date on which the OT candidate's home-shift
// assignment should live so the candidate qualifies as "working
// the shift adjacent to the gap".
//
// Context: home-shift assignments are stored on the date they
// START. For a wrap-midnight shift (e.g. Night 22:00-08:00) the
// shift that "just ended before the gap starts" actually started
// the PREVIOUS calendar day. So when Nights is the prev-candidate
// for a Day/4/25 gap, the Night officer's qualifying assignment
// sits on 4/24, not 4/25.
//
// role:
//   "prev"  — first-half covers (candidate shift is BEFORE the gap
//             in the rotation): if the candidate shift's clock
//             start is *after* the gap's clock start, the
//             candidate shift began the day before.
//   "next"  — second-half covers (candidate shift is AFTER the
//             gap in the rotation): if the candidate shift's clock
//             start is *before* the gap's clock start, it starts
//             the day after (Night gap → Day next).
export function candidateAssignmentDate(
  gapShift: Shift,
  candidateShift: Shift,
  gapDate: string,
  role: "prev" | "next",
): string {
  const gapStart = parseHHMM(gapShift.startTime);
  const candStart = parseHHMM(candidateShift.startTime);
  let offsetDays = 0;
  if (role === "prev" && candStart > gapStart) offsetDays = -1;
  if (role === "next" && candStart < gapStart) offsetDays = +1;
  if (offsetDays === 0) return gapDate;
  const d = new Date(gapDate + "T00:00:00");
  d.setDate(d.getDate() + offsetDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Which coverage-request types can the given officer submit against
// the (shortShiftId, date) shortage right now? Mirrors the eligibility
// buckets used by FixCoverageModal but scoped to a single officer so
// the dashboard Request button can decide whether to show at all and
// which coverage-type options to expose.
//
//   full          — officer is in the SAME department as the short
//                   shift, isn't rostered to it, isn't already
//                   assigned to it that day
//   split_first   — officer's home shift IS the previous adjacent
//                   shift and they're working that day
//   split_second  — officer's home shift IS the next adjacent shift
//                   and they're working that day
//
// Extra guards that veto every option:
//   * officer has approved time-off overlapping this date
//   * officer's home shift is exactly the short shift (they're
//     already scheduled to work it — a Request would be nonsense)
//   * officer already has an OT assignment on the short shift that
//     day (they're already covering the gap)
export function eligibleCoverageTypes(args: {
  employee: Employee;
  shortShiftId: string;
  date: string;
  allShifts: Shift[];
  // Only the current officer's assignments matter for the veto
  // checks; caller can pre-filter for performance.
  myAssignmentsOnDate: Assignment[];
  // Approved time-off rows for the current officer overlapping the
  // date. Caller pre-filters; the helper just checks for any hit.
  myApprovedTimeOffOnDate: TimeOff[];
}): CoverageRequestType[] {
  const short = args.allShifts.find((s) => s.id === args.shortShiftId);
  if (!short) return [];
  if (args.myApprovedTimeOffOnDate.length > 0) return [];

  const emp = args.employee;
  if (!emp || emp.status !== "active") return [];

  // Already scheduled ON the short shift that day, in any form —
  // home-shift default or an explicit assignment — means there's no
  // seat to claim by requesting coverage.
  if (emp.homeShiftId === args.shortShiftId) return [];
  if (args.myAssignmentsOnDate.some((a) =>
    a.shiftId === args.shortShiftId && a.status !== "cancelled"
  )) return [];

  const { prev, next } = adjacentShifts(args.allShifts, args.shortShiftId);
  const types: CoverageRequestType[] = [];

  // Same-department, non-adjacent officers can take the whole shift
  // as Full OT. Adjacent-shift officers get offered split instead of
  // full since split is the canonical "half from each side" pattern.
  const isPrevRoster = prev !== null && emp.homeShiftId === prev.id;
  const isNextRoster = next !== null && emp.homeShiftId === next.id;
  const sameDept = emp.departmentId === short.departmentId;

  if (sameDept && !isPrevRoster && !isNextRoster) {
    types.push("full");
  }
  if (isPrevRoster) types.push("split_first");
  if (isNextRoster) types.push("split_second");

  return types;
}

// Human-readable label for a coverage-request type. Used by both the
// request button popover and the supervisor review widget.
export function coverageRequestTypeLabel(t: CoverageRequestType): string {
  switch (t) {
    case "full":         return "Full shift";
    case "split_first":  return "First half (stay late)";
    case "split_second": return "Second half (come in early)";
  }
}
